/*
  O2 behavior for the pickup availability preview and drawer rendered by
  sections/pickup-availability-o2.liquid. The Dawn custom elements in
  pickup-availability.js call initPreview/initDrawer when this script is
  loaded; on Dawn-only pages the hooks are a no-op.
*/
window.PickupAvailabilityO2 =
  window.PickupAvailabilityO2 ||
  (() => {
    const COORDS_CACHE_MAX_AGE_MS = 300000;
    const GEOLOCATION_TIMEOUT_MS = 10000;
    const EARTH_RADIUS_KM = 6371;

    const SELECTORS = {
      drawer: 'pickup-availability-drawer[data-pickup-availability-o2]',
      disabledContainer: '[hidden], [inert]',
      granted: '[data-pickup-location-granted]',
      locationCheckbox: 'input[data-pickup-location-request]',
      loading: '[data-pickup-location-loading]',
      locationItem: '[data-pickup-availability-location]',
      locationList: '[data-store-availability-drawer-content]',
      nearestAddress: '[data-pickup-nearest-address]',
      nearestBadge: '[data-reservation-nearest]',
      nearestName: '[data-pickup-nearest-name]',
      noResults: '[data-pickup-availability-no-results]',
      overlay: '[data-pickup-availability-overlay]',
      pickupAvailability: 'pickup-availability',
      previewGranted: '[data-pickup-preview-granted]',
      previewNearestName: '[data-pickup-preview-nearest-name]',
      previewPrompt: '[data-pickup-preview-prompt]',
      prompt: '[data-pickup-location-prompt]',
      requestLocation: '[data-pickup-location-request]',
      reservationFlow: 'reservation-flow-o2',
      searchInput: '[data-pickup-availability-search]',
      searchControls: '[data-pickup-search-controls]',
      storeList: '[data-pickup-store-list]',
      resultsToggle: '[data-pickup-results-toggle]',
      resultsMore: '[data-pickup-results-more]',
      resultsLess: '[data-pickup-results-less]',
      resultsStatus: '[data-pickup-results-status]',
    };

    const EVENTS = { filtered: 'pickup:filtered' };

    let cachedCoords = null;
    let cachedCoordsAt = 0;

    function normalizeSearchText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .trim();
    }

    function compactText(value) {
      return value.replace(/\s+/g, '');
    }

    function watchGeolocationPermission(onState) {
      if (!navigator.permissions?.query) return;

      navigator.permissions
        .query({ name: 'geolocation' })
        .then((permissionStatus) => {
          onState(permissionStatus.state);
          permissionStatus.onchange = () => onState(permissionStatus.state);
        })
        .catch(() => {});
    }

    function setPreviewState(preview, isGranted, nearestLocation) {
      const nearestName = preview.querySelector(SELECTORS.previewNearestName);

      preview.querySelectorAll(SELECTORS.previewPrompt).forEach((element) => {
        element.hidden = isGranted;
      });

      preview.querySelectorAll(SELECTORS.previewGranted).forEach((element) => {
        element.hidden = !isGranted;
      });

      if (nearestName && nearestLocation?.name) nearestName.textContent = nearestLocation.name;
    }

    function toRadians(degrees) {
      return (degrees * Math.PI) / 180;
    }

    function getDistanceKm(lat1, lon1, lat2, lon2) {
      const dLat = toRadians(lat2 - lat1);
      const dLon = toRadians(lon2 - lon1);
      const avgLat = toRadians((lat1 + lat2) / 2);
      const x = dLon * Math.cos(avgLat);
      const distance = Math.sqrt(x * x + dLat * dLat) * EARTH_RADIUS_KM;

      return Math.round(distance * 100) / 100;
    }

    function findClosestLocation(drawer, coords) {
      const userLat = Number(coords.latitude);
      const userLon = Number(coords.longitude);
      let closest = null;
      let closestDistance = Infinity;

      drawer.querySelectorAll(SELECTORS.locationItem).forEach((location) => {
        if (!location.dataset.pickupLocationLatitude || !location.dataset.pickupLocationLongitude) return;

        const lat = Number(location.dataset.pickupLocationLatitude);
        const lon = Number(location.dataset.pickupLocationLongitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

        const distance = getDistanceKm(userLat, userLon, lat, lon);

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = {
            name: location.dataset.pickupLocationName,
            address: location.dataset.pickupLocationAddress,
          };
        }
      });

      return closest;
    }

    function showGrantedLocationState(drawer, coords) {
      const nearestLocation = findClosestLocation(drawer, coords);
      const prompt = drawer.querySelector(SELECTORS.prompt);
      const loading = drawer.querySelector(SELECTORS.loading);
      const granted = drawer.querySelector(SELECTORS.granted);
      const nearestName = drawer.querySelector(SELECTORS.nearestName);
      const nearestAddress = drawer.querySelector(SELECTORS.nearestAddress);

      if (!nearestLocation) {
        if (prompt) prompt.hidden = false;
        if (loading) loading.hidden = true;
        if (granted) granted.hidden = true;
        return;
      }

      if (prompt) prompt.hidden = true;
      if (loading) loading.hidden = true;
      if (granted) granted.hidden = false;
      if (nearestName && nearestLocation?.name) nearestName.textContent = nearestLocation.name;
      if (nearestAddress && nearestLocation?.address) nearestAddress.textContent = nearestLocation.address;

      const checkbox = drawer.querySelector(SELECTORS.locationCheckbox);

      if (checkbox) checkbox.checked = true;

      refreshSearch(drawer);

      document.querySelectorAll(SELECTORS.pickupAvailability).forEach((availability) => {
        setPreviewState(availability, true, nearestLocation);
      });
    }

    function getFreshCachedCoords() {
      if (!cachedCoords || Date.now() - cachedCoordsAt >= COORDS_CACHE_MAX_AGE_MS) return null;

      return cachedCoords;
    }

    function requestNearestLocation(drawer) {
      const prompt = drawer.querySelector(SELECTORS.prompt);
      const loading = drawer.querySelector(SELECTORS.loading);
      const granted = drawer.querySelector(SELECTORS.granted);

      if (!navigator.geolocation) {
        if (prompt) prompt.hidden = false;
        if (loading) loading.hidden = true;
        if (granted) granted.hidden = true;
        return;
      }

      const cachedCoordsForFallback = getFreshCachedCoords();

      if (cachedCoordsForFallback) {
        showGrantedLocationState(drawer, cachedCoordsForFallback);
      } else {
        if (prompt) prompt.hidden = true;
        if (loading) loading.hidden = false;
        if (granted) granted.hidden = true;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          cachedCoords = position.coords;
          cachedCoordsAt = Date.now();
          showGrantedLocationState(drawer, position.coords);
        },
        () => {
          if (cachedCoordsForFallback) return;

          const checkbox = drawer.querySelector(SELECTORS.locationCheckbox);

          if (checkbox) checkbox.checked = false;
          if (prompt) prompt.hidden = false;
          if (loading) loading.hidden = true;
          if (granted) granted.hidden = true;
        },
        { maximumAge: 0, timeout: GEOLOCATION_TIMEOUT_MS }
      );
    }

    function sortLocations(locations) {
      const coords = getFreshCachedCoords();

      if (!coords) return locations;

      const distance = (item) => {
        if (!item.dataset.pickupLocationLatitude || !item.dataset.pickupLocationLongitude) return Infinity;
        return getDistanceKm(
          coords.latitude,
          coords.longitude,
          Number(item.dataset.pickupLocationLatitude),
          Number(item.dataset.pickupLocationLongitude)
        );
      };

      return locations.sort((left, right) => distance(left) - distance(right));
    }

    function refreshSearch(drawer) {
      const searchInput = drawer.querySelector(SELECTORS.searchInput);

      if (!searchInput) return;

      const list = drawer.querySelector(SELECTORS.locationList);

      if (!list) return;

      const entries = Array.from(list.querySelectorAll(SELECTORS.locationItem));
      const noResults = drawer.querySelector(SELECTORS.noResults);
      const query = normalizeSearchText(searchInput.value);
      const compactQuery = compactText(query);
      let visibleCount = 0;

      entries.forEach((location, index) => {
        location.dataset.pickupOriginalOrder ??= String(index);
        const haystack = normalizeSearchText(location.dataset.pickupAvailabilitySearchText);
        const isVisible = query === '' || haystack.includes(query) || compactText(haystack).includes(compactQuery);

        location.hidden = !isVisible;

        if (isVisible) visibleCount += 1;
      });
      const ordered = entries.sort(
        (a, b) => Number(a.dataset.pickupOriginalOrder) - Number(b.dataset.pickupOriginalOrder)
      );

      if (!query) sortLocations(ordered);

      list.append(...ordered);
      const locatedEntries = entries.filter(
        (item) => item.dataset.pickupLocationLatitude && item.dataset.pickupLocationLongitude
      );
      const nearest = getFreshCachedCoords() ? sortLocations(locatedEntries)[0] : null;

      entries.forEach((item) => {
        const badge = item.querySelector(SELECTORS.nearestBadge);

        if (badge) badge.hidden = query !== '' || item !== nearest;
      });

      if (noResults) noResults.hidden = visibleCount !== 0 || entries.length === 0;

      drawer
        .querySelector(SELECTORS.reservationFlow)
        ?.dispatchEvent(new CustomEvent(EVENTS.filtered, { detail: { count: visibleCount } }));
    }

    function initSearch(drawer) {
      drawer.querySelector(SELECTORS.searchInput)?.addEventListener('input', () => refreshSearch(drawer));
      refreshSearch(drawer);
    }

    function initStoreList(root) {
      if (root.pickupStoreListO2Init) return;

      const searchInput = root.querySelector(SELECTORS.searchInput);

      if (!searchInput) return;

      root.pickupStoreListO2Init = true;
      const entries = Array.from(root.querySelectorAll(SELECTORS.locationItem)).map((location) => {
        const haystack = normalizeSearchText(location.dataset.pickupAvailabilitySearchText);

        return { location, haystack, compactHaystack: compactText(haystack) };
      });
      const noResults = root.querySelector(SELECTORS.noResults);
      const status = root.querySelector(SELECTORS.resultsStatus);
      const toggle = root.querySelector(SELECTORS.resultsToggle);
      const limit = Number(root.dataset.pickupVisibleLimit) || Infinity;
      let expanded = false;

      function updateResults() {
        const query = normalizeSearchText(searchInput.value);
        const compactQuery = compactText(query);
        let visibleCount = 0;

        entries.forEach(({ location, haystack, compactHaystack }) => {
          const matches = query === '' || haystack.includes(query) || compactHaystack.includes(compactQuery);

          if (matches) visibleCount += 1;

          location.hidden = !matches || (!expanded && visibleCount > limit);
        });

        if (noResults) noResults.hidden = visibleCount !== 0;
        if (status) status.textContent = visibleCount === 0 ? noResults?.textContent.trim() || '' : '';

        if (toggle) {
          toggle.hidden = visibleCount <= limit;
          toggle.setAttribute('aria-expanded', String(expanded));
          root.querySelector(SELECTORS.resultsMore).hidden = expanded;
          root.querySelector(SELECTORS.resultsLess).hidden = !expanded;
        }
      }

      searchInput.addEventListener('input', () => {
        expanded = false;
        updateResults();
      });
      toggle?.addEventListener('click', () => {
        expanded = !expanded;
        updateResults();
      });
      updateResults();
      root.querySelector(SELECTORS.searchControls)?.removeAttribute('hidden');
    }

    function initPreview(preview) {
      if (!preview.querySelector(SELECTORS.previewPrompt)) return;

      const coords = getFreshCachedCoords();
      const drawer = document.querySelector(SELECTORS.drawer);

      if (coords && drawer) {
        const nearest = findClosestLocation(drawer, coords);

        setPreviewState(preview, Boolean(nearest), nearest);
      }

      if (preview.pickupAvailabilityO2Init) return;

      preview.pickupAvailabilityO2Init = true;

      watchGeolocationPermission((state) => {
        if (state !== 'granted') setPreviewState(preview, false);
      });
    }

    function initDrawer(drawer) {
      if (!drawer.matches(SELECTORS.drawer)) return;
      if (drawer.pickupAvailabilityO2Init) return;

      drawer.pickupAvailabilityO2Init = true;

      drawer.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;

        const controls = getFocusableElements(drawer).filter(
          (element) => !element.closest(SELECTORS.disabledContainer) && element.getClientRects().length
        );
        const first = controls[0];
        const last = controls[controls.length - 1];

        if (!first) return;

        if (
          (!event.shiftKey && document.activeElement === last) ||
          (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement)))
        ) {
          event.preventDefault();
          event.stopPropagation();
          (event.shiftKey ? last : first).focus();
        }
      });
      drawer.querySelector(SELECTORS.overlay)?.addEventListener('click', () => drawer.hide());
      drawer.querySelector(SELECTORS.requestLocation)?.addEventListener('click', (event) => {
        if (event.target.type === 'checkbox' && !event.target.checked) {
          cachedCoords = null;
          refreshSearch(drawer);

          document.querySelectorAll(SELECTORS.pickupAvailability).forEach((preview) => setPreviewState(preview, false));
          return;
        }
        requestNearestLocation(drawer);
      });
      initSearch(drawer);

      drawer.querySelectorAll(SELECTORS.storeList).forEach(initStoreList);

      watchGeolocationPermission((state) => {
        if (state === 'granted') {
          requestNearestLocation(drawer);
          return;
        }

        const prompt = drawer.querySelector(SELECTORS.prompt);
        const loading = drawer.querySelector(SELECTORS.loading);
        const granted = drawer.querySelector(SELECTORS.granted);

        if (prompt) prompt.hidden = false;
        if (loading) loading.hidden = true;
        if (granted) granted.hidden = true;
      });
    }

    // Wire up anything rendered before this script executed, so init does not
    // depend on script order relative to pickup-availability.js.
    document.querySelectorAll(SELECTORS.drawer).forEach(initDrawer);

    document.querySelectorAll(SELECTORS.pickupAvailability).forEach(initPreview);

    document.querySelectorAll(SELECTORS.storeList).forEach(initStoreList);

    return { initPreview, initDrawer, initStoreList, refreshSearch, sortLocations };
  })();
